import React, { useState } from "react";
import { Alert, Modal, Space, Switch, Typography } from "antd";
import { setPasswordLogin } from "../../../api/auth/mfaApi";
import { authFailure, authFailureMessage, isStepUpDeclined } from "../../../lib/http/authErrors";

const { Text } = Typography;

interface PasswordLoginSettingProps {
	enabled: boolean;
	hasTotp: boolean;
	hasPasskey: boolean;
	onChanged: () => void;
}

const failureText = (error: unknown): string => {
	const failure = authFailure(error);
	if (failure.code === "conflict" && failure.details.reason === "role_policy") return "Your role signs in with a passkey only while you have one.";
	if (failure.status === 422) return "Add an authenticator app or a passkey first.";
	return authFailureMessage(error);
};

// PUT /auth/password-login (contract §6.8): off only while a strong factor exists; identity re-checks and asks for a step-up.
const PasswordLoginSetting: React.FC<PasswordLoginSettingProps> = ({ enabled, hasTotp, hasPasskey, onChanged }) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const hasStrongFactor = hasTotp || hasPasskey;

	const apply = async (next: boolean) => {
		setBusy(true);
		setError("");
		try {
			await setPasswordLogin(next);
			onChanged();
		} catch (err) {
			if (!isStepUpDeclined(err)) setError(failureText(err));
		} finally {
			setBusy(false);
		}
	};

	const onToggle = (next: boolean) => {
		if (next) {
			void apply(true);
			return;
		}
		Modal.confirm({
			title: "Turn off password sign-in?",
			content: hasPasskey
				? "You will sign in with your passkey. If you lose access to it, a password reset email turns password sign-in back on."
				: "You have no passkey, and an authenticator app is a second step, not a way to sign in on its own. With password sign-in off, the only way back in is a password reset email.",
			okText: "Turn off",
			okType: "danger",
			cancelText: "Keep it on",
			onOk: () => apply(false),
		});
	};

	return (
		<div data-testid="security-password-login">
			<Space align="start" size="middle">
				<Switch
					data-testid="security-password-login-toggle"
					checked={enabled}
					// Turning it back on is never blocked; turning it off needs a strong factor.
					disabled={busy || (enabled && !hasStrongFactor)}
					loading={busy}
					onChange={onToggle}
					aria-label="Allow sign-in with a password"
				/>
				<div>
					<Text strong>Allow sign-in with a password</Text>
					<br />
					<Text type="secondary">
						{hasStrongFactor
							? enabled
								? "Turn this off to sign in with your passkey only."
								: "Password sign-in is off. A password reset email turns it back on."
							: "Add an authenticator app or a passkey before turning this off."}
					</Text>
				</div>
			</Space>
			{error && <Alert data-testid="security-password-login-error" type="error" showIcon message={error} style={{ marginTop: "var(--mg-sp-6)" }} />}
		</div>
	);
};

export default PasswordLoginSetting;
